import React, {
  useEffect,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import { v4 as uuid } from 'uuid';
import { Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import TextField from '../../components/Inputs/TextField';
import Select from '../../components/Inputs/Select/Select';
import Box from '../../components/Styled/Box';
import Button from '../../components/Buttons/Button';
import FormCard from '../../components/Cards/FormCard';
import Typography from '../../components/Typography/Typography';
import CpfField from '../../components/Inputs/CpfField';

import { createEnrollment } from '../../services/apis/respondents/enrollments.respondents.api';
import { statusByToken } from '../../services/apis/respondents/applications-questionnaires.api';

import { isValidCpf } from '../../utils/validators';
import { useRoutes, useIsMobileLayout } from '../../hooks';

import { cacheActions } from '../../redux/actions';

import { fetchSelectorsList } from '../../services/apis/selectors_list.api';
import { cachesSelectors, userSelectors } from '../../redux/selectors';

const mountOptions = (options) => options.map((item) => ({ id: item, name: item }));

const validationSchema = (f, questionnaireFields) => Yup.object().shape({
  email: Yup.lazy(() => {
    if (get(questionnaireFields, 'email.required')) {
      return Yup.string()
        .email(f({ id: 'errors.invalid.email' }))
        .required(f({ id: 'errors.required.simple' }));
    }
    return Yup.string().email(f({ id: 'errors.invalid.email' }));
  }),
  first_name: Yup.lazy(() => {
    if (get(questionnaireFields, 'first_name.required')) {
      return Yup.string().trim().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.string();
  }),
  last_name: Yup.lazy(() => {
    if (get(questionnaireFields, 'last_name.required')) {
      return Yup.string().trim().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.string();
  }),
  cpf: Yup.lazy(() => {
    if (get(questionnaireFields, 'cpf.required')) {
      return Yup.string()
        .test('cpf', f({ id: 'errors.invalid.cpf' }), isValidCpf)
        .required(f({ id: 'errors.required.simple' }));
    }
    return Yup.string().test('cpf', f({ id: 'errors.invalid.cpf' }), isValidCpf);
  }),
  working_company_name: Yup.lazy(() => {
    if (get(questionnaireFields, 'working_company_name.required')) {
      return Yup.string().trim().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.string();
  }),
  working_company_job_title: Yup.lazy(() => {
    if (get(questionnaireFields, 'working_company_job_title.required')) {
      return Yup.mixed().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.mixed();
  }),
  working_company_department: Yup.lazy(() => {
    if (get(questionnaireFields, 'working_company_department.required')) {
      return Yup.mixed().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.mixed();
  }),
  gender: Yup.lazy(() => {
    if (get(questionnaireFields, 'gender.required')) {
      return Yup.mixed().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.mixed();
  }),
  biological_sex: Yup.lazy(() => {
    if (get(questionnaireFields, 'biological_sex.required')) {
      return Yup.mixed().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.mixed();
  }),
  sexual_orientation: Yup.lazy(() => {
    if (get(questionnaireFields, 'sexual_orientation.required')) {
      return Yup.mixed().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.mixed();
  }),
  color: Yup.lazy(() => {
    if (get(questionnaireFields, 'color.required')) {
      return Yup.mixed().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.mixed();
  }),
  pcd: Yup.lazy(() => {
    if (get(questionnaireFields, 'pcd.required')) {
      return Yup.mixed().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.mixed();
  }),
});

const initialValues = {
  email: '',
  working_company_name: '',
  working_company_job_title: '',
  working_company_department: '',
  first_name: '',
  last_name: '',
  cpf: '',
  gender: '',
  biological_sex: '',
  sexual_orientation: '',
  color: '',
  pcd: '',
};

const defaultOptions = {
  job_titles: [],
  departments: [],
  genders: [],
  biological_sex: [],
  sexual_orientation: [],
  color: [],
  pcd: [],
};

const getInitialOptions = (cached, language) => {
  if (!cached?.[language]) {
    return defaultOptions;
  }

  return cached[language];
};

// FIXME: esse email deve ser gerado no back
const getPayload = (values) => ({
  working_company_name: values.working_company_name || '',
  working_company_job_title: get(values, 'working_company_job_title.name', ''),
  working_company_department: get(values, 'working_company_department.name', ''),
  email: values.email || `respondente_externo_${uuid()}@example.com`,
  first_name: values.first_name || '',
  last_name: values.last_name || '',
  cpf: values.cpf || '',
  gender: get(values, 'gender.name', ''),
  biological_sex: get(values, 'biological_sex.name', ''),
  sexual_orientation: get(values, 'sexual_orientation.name', ''),
  color: get(values, 'color.name', ''),
  pcd: get(values, 'pcd.name', ''),
});

const SELECT_OPTIONS_KEY = btoa('SELECT_OPTIONS');

const defaultField = { visible: false, required: false };
const getInitialFields = () => Object.keys(initialValues)
  .reduce((obj, key) => ({ ...obj, [key]: defaultField }), {});

const EnrollQuestionnaireForm = ({
  match,
  history,
}) => {
  const dispatch = useDispatch();
  const routes = useRoutes();
  const { formatMessage: f, messages } = useIntl();
  const isMobile = useIsMobileLayout();

  const activeLanguage = useSelector(userSelectors.activeLanguage);
  const cachedOptions = useSelector(cachesSelectors.cachedValue(SELECT_OPTIONS_KEY));

  const applicationQuestionnaireToken = match.params.token;

  const [preventRender, setPreventRender] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [questionnaireFields, setQuestionnaireFields] = useState(getInitialFields());
  const [options, setOptions] = useState(getInitialOptions(cachedOptions, activeLanguage));

  const onSubmit = React.useCallback(async (values) => {
    const enrollment = getPayload(values);
    const { data } = await createEnrollment({
      enrollment,
      params: 'fields=hashid',
      applicationQuestionnaireToken,
      apiArgs: { apiOptions: { options: { disableHeaders: true } } },
    });

    history.push({
      pathname: routes.public.questionnaire,
      state: {
        enrollmentId: data.hashid,
        applicationQuestionnaireToken,
      },
    });
  }, [routes, applicationQuestionnaireToken, history]);

  const formik = useFormik({
    onSubmit,
    validateOnMount: true,
    enableReinitialize: true,
    initialValues,
    validationSchema: validationSchema(f, questionnaireFields),
  });

  const disabledButton = (
    !formik.isValid
    || formik.isSubmitting
  );

  const fetchQuestionnaireStatus = useCallback(async () => {
    try {
      const { application_questionnaire_status, ...data } = await statusByToken({
        applicationQuestionnaireToken,
        link_type: 'open',
        apiArgs: {
          options: {
            toggleDialog: false,
            toggleLoading: false,
          },
        },
      });

      const isQuestionnaireExpired = [
        'ended',
        'not_answered',
        'results_available',
      ].includes(application_questionnaire_status);

      const hasSomeFieldVisible = Object.values(data)
        .filter((d) => typeof d === 'object')
        .find((field) => field.visible);

      if (!hasSomeFieldVisible && !isQuestionnaireExpired) {
        onSubmit({});
      }

      else {
        setIsExpired(isQuestionnaireExpired);
        setQuestionnaireFields(data);
        setPreventRender(false);
      }
    }
    catch (error) {
      history.push(routes.messages.error404);
    }
  }, [applicationQuestionnaireToken, history, onSubmit, routes.messages.error404]);

  useEffect(() => {
    fetchQuestionnaireStatus();
  }, [fetchQuestionnaireStatus]);

  useEffect(() => {
    const fetchSelectors = async () => {
      const { data } = await fetchSelectorsList({
        apiArgs: { options: { toggleLoading: false } },
      });
      setOptions({
        job_titles: [...mountOptions(data.working_company_job_title_options), { id: 'words.do_not_apply', name: messages['words.do_not_apply'] }],
        departments: [...mountOptions(data.working_company_department_options), { id: 'words.do_not_apply', name: messages['words.do_not_apply'] }],
        genders: mountOptions(data.gender_options),
        biological_sex: mountOptions(data.biological_sex_options),
        sexual_orientation: mountOptions(data.sexual_orientation_options),
        color: mountOptions(data.color_options),
        pcd: mountOptions(data.pcd_options),
      });
      dispatch(cacheActions.cacheValue(SELECT_OPTIONS_KEY, {
        ...cachedOptions,
        [activeLanguage]: {
          job_titles: [...mountOptions(data.working_company_job_title_options), { id: 'words.do_not_apply', name: messages['words.do_not_apply'] }],
          departments: [...mountOptions(data.working_company_department_options), { id: 'words.do_not_apply', name: messages['words.do_not_apply'] }],
          genders: mountOptions(data.gender_options),
          biological_sex: mountOptions(data.biological_sex_options),
          sexual_orientation: mountOptions(data.sexual_orientation_options),
          color: mountOptions(data.color_options),
          pcd: mountOptions(data.pcd_options),
        },
      }));
    };
    if (!(cachedOptions || {})[activeLanguage]) {
      fetchSelectors();
    }
  }, [dispatch, cachedOptions, messages, activeLanguage]);

  if (isExpired) {
    return <Redirect to={routes.messages.expired} />;
  }

  const formContent = (
    <form onSubmit={formik.handleSubmit}>
      {get(questionnaireFields, 'first_name.visible') && (
        <TextField
          required={get(questionnaireFields, 'first_name.required')}
          boxProps={{ mt: 3 }}
          name='first_name'
          value={formik.values.first_name}
          onChange={formik.handleChange('first_name')}
          label={f({ id: 'public_pages.complete_register.first_name_label' })}
          placeholder={f({ id: 'public_pages.complete_register.first_name_placeholder' })}
          error={formik.touched.first_name ? formik.errors.first_name : ''}
        />
      )}

      {get(questionnaireFields, 'last_name.visible') && (
        <TextField
          required={get(questionnaireFields, 'last_name.required')}
          boxProps={{ mt: 3 }}
          name='last_name'
          value={formik.values.last_name}
          onChange={formik.handleChange('last_name')}
          label={f({ id: 'public_pages.complete_register.last_name_label' })}
          placeholder={f({ id: 'public_pages.complete_register.last_name_placeholder' })}
          error={formik.touched.last_name ? formik.errors.last_name : ''}
        />
      )}

      {get(questionnaireFields, 'email.visible') && (
        <TextField
          required={get(questionnaireFields, 'email.required')}
          boxProps={{ mt: 3 }}
          name='email'
          type='email'
          value={formik.values.email}
          onChange={formik.handleChange('email')}
          label={f({ id: 'public_pages.complete_register.email_label' })}
          placeholder={f({ id: 'public_pages.complete_register.email_placeholder' })}
          error={formik.touched.email ? formik.errors.email : ''}
        />
      )}

      {get(questionnaireFields, 'working_company_name.visible') && (
        <TextField
          required={get(questionnaireFields, 'working_company_name.required')}
          boxProps={{ mt: 3 }}
          name='working_company_name'
          type='working_company_name'
          value={formik.values.working_company_name}
          onChange={formik.handleChange('working_company_name')}
          label={f({ id: 'public_pages.complete_register.working_company_name_label' })}
          placeholder={f({ id: 'public_pages.complete_register.working_company_name_placeholder' })}
          error={formik.touched.working_company_name ? formik.errors.working_company_name : ''}
        />
      )}

      {get(questionnaireFields, 'working_company_department.visible') && (
        <Select
          required={get(questionnaireFields, 'working_company_department.required')}
          options={options.departments}
          boxProps={{ mt: 3 }}
          name='working_company_department'
          type='working_company_department'
          value={formik.values.working_company_department}
          onChange={(val) => formik.setFieldValue('working_company_department', val)}
          label={f({ id: 'public_pages.complete_register.working_company_department_label' })}
          placeholder={f({ id: 'public_pages.complete_register.working_company_department_placeholder' })}
          error={formik.touched.working_company_department ? formik.errors.working_company_department : ''}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      )}

      {get(questionnaireFields, 'working_company_job_title.visible') && (
        <Select
          required={get(questionnaireFields, 'working_company_job_title.required')}
          options={options.job_titles}
          boxProps={{ mt: 3 }}
          name='working_company_job_title'
          type='working_company_job_title'
          value={formik.values.working_company_job_title}
          onChange={(val) => formik.setFieldValue('working_company_job_title', val)}
          label={f({ id: 'public_pages.complete_register.working_company_job_title_label' })}
          placeholder={f({ id: 'public_pages.complete_register.working_company_job_title_placeholder' })}
          error={formik.touched.working_company_job_title ? formik.errors.working_company_job_title : ''}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      )}

      {get(questionnaireFields, 'cpf.visible') && (
        <CpfField
          required={get(questionnaireFields, 'cpf.required')}
          tooltip={f({ id: 'public_pages.complete_register.cpf_tooltip' })}
          boxProps={{ mt: 3 }}
          name='cpf'
          value={formik.values.cpf}
          onChange={formik.handleChange('cpf')}
          onBlur={formik.handleBlur('cpf')}
          label={f({ id: 'public_pages.complete_register.cpf_label' })}
          placeholder={f({ id: 'public_pages.complete_register.cpf_placeholder' })}
          error={formik.touched.cpf ? formik.errors.cpf : ''}
        />
      )}

      {get(questionnaireFields, 'biological_sex.visible') && (
        <Select
          required={get(questionnaireFields, 'biological_sex.required')}
          options={options.biological_sex}
          boxProps={{ mt: 3 }}
          name='biological_sex'
          value={formik.values.biological_sex}
          onChange={(val) => formik.setFieldValue('biological_sex', val)}
          label={f({ id: 'public_pages.complete_register.biological_sex_label' })}
          placeholder={f({ id: 'public_pages.complete_register.biological_sex_placeholder' })}
          error={formik.touched.biological_sex ? formik.errors.biological_sex : ''}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      )}

      {get(questionnaireFields, 'gender.visible') && (
        <Select
          required={get(questionnaireFields, 'gender.required')}
          tooltip={f({ id: 'public_pages.complete_register.gender_tooltip' })}
          options={options.genders}
          boxProps={{ mt: 3 }}
          name='gender'
          value={formik.values.gender}
          onChange={(val) => formik.setFieldValue('gender', val)}
          label={f({ id: 'public_pages.complete_register.gender_label' })}
          placeholder={f({ id: 'public_pages.complete_register.gender_placeholder' })}
          error={formik.touched.gender ? formik.errors.gender : ''}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      )}

      {get(questionnaireFields, 'sexual_orientation.visible') && (
        <Select
          required={get(questionnaireFields, 'sexual_orientation.required')}
          options={options.sexual_orientation}
          tooltip={f({ id: 'public_pages.complete_register.sexual_orientation_tooltip' })}
          boxProps={{ mt: 3 }}
          name='sexual_orientation'
          value={formik.values.sexual_orientation}
          onChange={(val) => formik.setFieldValue('sexual_orientation', val)}
          label={f({ id: 'public_pages.complete_register.sexual_orientation_label' })}
          placeholder={f({ id: 'public_pages.complete_register.sexual_orientation_placeholder' })}
          error={formik.touched.sexual_orientation ? formik.errors.sexual_orientation : ''}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      )}

      {get(questionnaireFields, 'color.visible') && (
        <Select
          required={get(questionnaireFields, 'color.required')}
          options={options.color}
          tooltip={f({ id: 'public_pages.complete_register.color_tooltip' })}
          boxProps={{ mt: 3 }}
          name='color'
          value={formik.values.color}
          onChange={(val) => formik.setFieldValue('color', val)}
          label={f({ id: 'public_pages.complete_register.color_label' })}
          placeholder={f({ id: 'public_pages.complete_register.color_placeholder' })}
          error={formik.touched.color ? formik.errors.color : ''}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      )}

      {get(questionnaireFields, 'pcd.visible') && (
        <Select
          required={get(questionnaireFields, 'pcd.required')}
          options={options.pcd}
          tooltip={isMobile ? null : (
            <>
              <b>
                {f({ id: 'public_pages.complete_register.pcd_tooltip_pt1_title' })}
                {': '}
              </b>
              {f({ id: 'public_pages.complete_register.pcd_tooltip_pt1_body' })}
              <br />
              <br />

              <b>
                {f({ id: 'public_pages.complete_register.pcd_tooltip_pt2_title' })}
                {': '}
              </b>
              {f({ id: 'public_pages.complete_register.pcd_tooltip_pt2_body' })}
              <br />
              <br />

              <b>
                {f({ id: 'public_pages.complete_register.pcd_tooltip_pt3_title' })}
                {': '}
              </b>
              {f({ id: 'public_pages.complete_register.pcd_tooltip_pt3_body' })}
              <br />
              <br />

              <b>
                {f({ id: 'public_pages.complete_register.pcd_tooltip_pt4_title' })}
                {': '}
              </b>
              {f({ id: 'public_pages.complete_register.pcd_tooltip_pt4_body' })}
            </>
          )}
          tooltipProps={{
            tooltip: {
              maxWidth: 480,
              textAlign: 'left',
            },
          }}
          boxProps={{ mt: 3 }}
          name='pcd'
          value={formik.values.pcd}
          onChange={(val) => formik.setFieldValue('pcd', val)}
          label={f({ id: 'public_pages.complete_register.pcd_label' })}
          placeholder={f({ id: 'public_pages.complete_register.pcd_placeholder' })}
          error={formik.touched.pcd ? formik.errors.pcd : ''}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      )}

      <Box
        mt='40px'
        display='flex'
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent={isMobile ? 'flex-start' : 'flex-end'}
      >
        <Button
          width={isMobile ? '75%' : ''}
          centerText={isMobile}
          type='submit'
          disabled={disabledButton}
        >
          {f({ id: 'words.save' })}
        </Button>
      </Box>
    </form>
  );

  if (preventRender) {
    return (
      <Box width='100%'>
        <Skeleton width='100%' height={800} />
      </Box>
    );
  }

  return (
    isMobile
      ? (
        <Box width='100%' mt='24px'>
          <Box px='24px' pt='16px'>
            <Typography
              block
              variant='h4'
              color='coldgrey600'
            >
              {f({ id: 'public_pages.complete_register.title' })}
            </Typography>

            <Typography
              block
              mt={3}
              color='coldgrey600'
              variant='body'
            >
              {f({ id: 'public_pages.complete_register.subtitle' })}
            </Typography>
          </Box>
          <Box px='24px' pt='16px'>
            {formContent}
          </Box>
        </Box>
      )
      : (
        <FormCard
          title={f({ id: 'public_pages.complete_register.title' })}
          subtitle={f({ id: 'public_pages.complete_register.subtitle' })}
          cardProps={{ overflow: 'auto' }}
        >
          {formContent}
        </FormCard>
      )
  );
};

EnrollQuestionnaireForm.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default EnrollQuestionnaireForm;
